import { useLazyFetch } from 'src/hooks/useLazyFetch'
import { useSwrFetch } from 'src/hooks/useSwrFetch'
import { useSwrLazyFetch } from 'src/hooks/useSwrLazyFetch'
import { isJSON } from 'src/utils'

export interface IPkIdAndName {
  pk_id: number
  name: string
}

export interface ICourseInfo {
  pk_id: number
  name: string
  year: number
  manual_url: string
  logo_url: string
  terms_url: string
  min_birthdate: Date
  max_birthdate: Date
  start_process: Date
  end_process: Date
}

export interface IAddress {
  postalCode: string
  publicPlace: string
  number: number
  complement: string
  neighborhood: string
  city: string
  ufState: string
}

export interface IResearch {
  schoolId: number
  schoolMetTypeId: number
  schoolPeriodId: number
  schoolTypeId: number
}

export interface IDocument {
  isForeign: boolean
  registerCode: string
  issueStateId: number
  issueAgencyId: number
  issueDate: string | Date
}

export interface ICandidateFile {
  fileId?: number
  fileName: string
  fileUrl: string
}

export interface IResponsible {
  relationTypeId: number
  document: IDocument
  responsibleInfo: IPerson
}

export interface IPerson {
  name: string
  socialName?: string
  cpf: string
  cellphone: string
  birthDate: string | Date
  sexTypeId: number
  email: string
  address: IAddress
  nacionalityTypeId: number
}

export interface ICandidateInfo extends IPerson {
  motherName: string
  fatherName: string
}

export interface ICandidate {
  document: IDocument
  research: IResearch
  candidateFile?: ICandidateFile
  candidateInfo: ICandidateInfo
  courseTypeId: number
  responsibles: IResponsible[]
}

export interface IValidateLoginJWT {
  candidateCode: string
  token: string
}

export interface ICandidateLogin {
  candidateCode: string
  date: string
}

export interface IUpdateCandidateGeneralInfo {
  document?: {
    documentId?: number
    isForeign?: boolean
    registerCode?: string
    issueState?: string
    issueAgency?: string
    issueDate?: Date
  }
  candidateInfo?: {
    personId?: number
    name?: string
    socialName?: string
    cellphone?: string
    birthDate?: Date
    sexTypeId?: number
    email?: string
    address?: {
      postalCode?: string
      publicPlace?: string
      number?: number
      complement?: string
      neighborhood?: string
      city?: string
      ufState?: string
    }
  }
  responsibles?: {
    document?: {
      documentId?: number
      isForeign?: boolean
      registerCode?: string
      issueState?: string
      issueAgency?: string
      issueDate?: Date
    }
    responsibleInfo?: {
      personId?: number
      name?: string
      socialName?: string
      cellphone?: string
      birthDate?: Date
      sexTypeId?: number
      email?: string
      address?: {
        postalCode?: string
        publicPlace?: string
        number?: number
        complement?: string
        neighborhood?: string
        city?: string
        ufState?: string
      }
    }
    relationTypeId?: number
  }[]
}

export type TFileStatuses = 'Em analise' | 'Aprovado' | 'Reprovado'

export type TPhasesStatuses = 'Em analise' | 'Aprovado' | 'Reprovado'

const BASE_URL = process.env.REACT_APP_REGISTRATION_BACKEND_BASE_URL
const BASE_PYTHON_URL = process.env.REACT_APP_PYTHON_BASE_URL

export const useGetCandidateByCpfLazy = () => {
  const [execute, results] = useSwrLazyFetch({
    cachingKey: `${BASE_URL}/candidate/cpf`,
  })

  return [
    async (cpf: string, courseId: number) => {
      await execute(`${BASE_URL}/candidate/${cpf}?courseId=${courseId}`)
    },
    results,
  ] as const
}

export const usePostValidateLoginJWT = () => {
  const [validateToken, postValidadeTokenResult] = useLazyFetch()

  return [
    (body: IValidateLoginJWT) => {
      validateToken({
        url: `${BASE_URL}/candidate/home/login/validate`,
        method: 'POST',
        body: isJSON(body) ? body : JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      })
    },
    postValidadeTokenResult,
  ] as const
}

export const useSaveCandidate = () => {
  const [saveCandidate, saveCandidateResult] = useLazyFetch()

  return [
    (body: ICandidate) => {
      saveCandidate({
        url: `${BASE_URL}/candidate/register?lang=pt`,
        method: 'POST',
        body: isJSON(body) ? body : JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      })
    },
    saveCandidateResult,
  ] as const
}

export const useSaveCandidateDocumentNew = () => {
  const [saveCandidateDocument, saveCandidateDocumentResult] = useLazyFetch()

  return [
    async (file: any) => {
      const formData = new FormData()
      formData.append('file', file)
      await saveCandidateDocument({
        url: `${BASE_PYTHON_URL}/upload`,
        method: 'POST',
        body: formData,
      })
    },
    saveCandidateDocumentResult,
  ] as const
}

export const useUpdateCandidateDocument = () => {
  const [updateCandidateDocument, updateCandidateDocumentResult] =
    useLazyFetch()

  return [
    async (jwt: string, candidateId: number, body: ICandidateFile) => {
      await updateCandidateDocument({
        url: `${BASE_URL}/file/upload/${candidateId}`,
        method: 'POST',
        body: isJSON(body) ? body : JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
      })
    },
    updateCandidateDocumentResult,
  ] as const
}

export const useGetSexTypes = () =>
  useSwrFetch<{ response: Array<{ pk_id: number; name: string }> }>({
    url: `${BASE_URL}/filter/sex`,
  })

export const useGetResponsibleRelationships = () =>
  useSwrFetch({
    url: `${BASE_URL}/filter/relation-type`,
  })

export const useGetSchoolPeriods = () =>
  useSwrFetch({
    url: `${BASE_URL}/filter/school-period-type`,
  })

export const useGetInstitutionTypes = () =>
  useSwrFetch({
    url: `${BASE_URL}/filter/school-type`,
  })

export const useGetSchoolMetTypes = () =>
  useSwrFetch({
    url: `${BASE_URL}/filter/school-met-type`,
  })

export const useGetStates = () =>
  useSwrFetch<{ response: Array<IPkIdAndName & { acronym: string }> }>({
    url: `${BASE_URL}/filter/state`,
  })

export const useGetIssuingAgencies = () =>
  useSwrFetch<{
    response: Array<Pick<IPkIdAndName, 'pk_id'> & { acronym: string }>
  }>({
    url: `${BASE_URL}/filter/issuing-agencies`,
  })

export const useGetNationalities = () =>
  useSwrFetch<{
    response: IPkIdAndName[]
  }>({
    url: `${BASE_URL}/filter/nationality`,
  })

export const useGetMaritalStatusTypes = () =>
  useSwrFetch<{
    response: IPkIdAndName[]
  }>({
    url: `${BASE_URL}/filter/marital-status-types`,
  })

export const useGetCitiesByState = () => {
  const [execute, results] = useSwrLazyFetch({
    cachingKey: `${BASE_URL}/filter/state/id/city`,
  })

  return [
    async (stateId: number) => {
      await execute(`${BASE_URL}/filter/state/${stateId}/city`)
    },
    results,
  ] as const
}

export const useGetSchoolsByCity = () => {
  const [execute, results] = useSwrLazyFetch({
    cachingKey: `${BASE_URL}/filter/state/stateId/city/cityId/school`,
  })

  return [
    async (stateId: number, cityId: number) => {
      await execute(`${BASE_URL}/filter/state/${stateId}/city/${cityId}/school`)
    },
    results,
  ] as const
}

export const useValidateWhatsApp = () => {
  const [validateWhatsApp, validateWhatsAppResult] = useLazyFetch()

  return [
    (phone: string) => {
      validateWhatsApp({
        url: `${BASE_URL}/whatsapp/validate`,
        method: 'POST',
        body: JSON.stringify({
          phone,
        }),
        headers: { 'Content-Type': 'application/json' },
      })
    },
    validateWhatsAppResult,
  ] as const
}

export const useGetCandidateLoginToken = () => {
  const [execute, results] = useLazyFetch()

  return [
    (body: ICandidateLogin) => {
      execute({
        url: `${BASE_URL}/candidate/home/login`,
        method: 'POST',
        body: isJSON(body) ? body : JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      })
    },
    results,
  ] as const
}

export const useGetPersonInformationLazy = () => {
  const [execute, results] = useLazyFetch()

  return [
    async (jwt: string) => {
      await execute({
        url: `${BASE_URL}/candidate/home/info`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
      })
    },
    results,
  ] as const
}

export const useGetRegisterTypesLazy = () => {
  const [execute, results] = useSwrLazyFetch({
    cachingKey: `${BASE_URL}/filter/course-type`,
  })

  return [
    async () => {
      await execute(`${BASE_URL}/filter/course-type`)
    },
    results,
  ] as const
}

export const useGetSettings = () => {
  const [execute, results] = useSwrLazyFetch({
    cachingKey: `${BASE_URL}/settings`,
  })

  return [
    async () => {
      await execute(`${BASE_URL}/settings?systemId=1`)
    },
    results,
  ] as const
}

export const useGetCandidateInfoLazy = () => {
  const [execute, results] = useLazyFetch()

  return [
    (jwt: string, candidateCode: string) => {
      execute({
        url: `${BASE_URL}/candidate/${candidateCode}/info`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
      })
    },
    results,
  ] as const
}

export const useGetCandidateResponsiblesInfoLazy = () => {
  const [execute, results] = useLazyFetch()

  return [
    (jwt: string, candidateCode: string) => {
      execute({
        url: `${BASE_URL}/candidate/${candidateCode}/info/responsible`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
      })
    },
    results,
  ] as const
}

export const useUpdateCandidateGeneralInfo = () => {
  const [execute, results] = useLazyFetch()

  return [
    (jwt: string, registerCode: string, body: IUpdateCandidateGeneralInfo) => {
      execute({
        url: `${BASE_URL}/candidate/${registerCode}/update`,
        method: 'POST',
        body: isJSON(body) ? body : JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
      })
    },
    results,
  ] as const
}
